import { byIdsResultJSONZ, ByIdsResultJSON } from "_shared/_api/by-ids/by-ids-result";
import { listCountsHook, listCountsTags } from "_shared/_api/list-counts/list-counts-hook";
import { api } from "_shared/_stores/api";
import z from "zod";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import FeeScheduleTemplateSchema, { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import {
  FeeScheduleTemplateListCountsJSONZ,
  type FeeScheduleTemplateListCounts,
} from "work/fee-schedule-templates/_api/queries/_schema/fee-schedule-template-list-counts-schema";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import FeeScheduleTemplateCommand, {
  FeeScheduleTemplateCategorySource,
} from "work/fee-schedule-templates/_api/commands/create-fee-schedule-template-api-request";
import UpdateFeeScheduleTemplateCommand from "work/fee-schedule-templates/_api/commands/update-fee-schedule-template-command";
import FeeScheduleCategorySchema, { FeeScheduleCategoryJSON } from "_shared/fee-schedules/categories/fee-schedule-category-schema";
import FeeScheduleCategoryCommand from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-category-command";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { withoutFeeScheduleTeamMember } from "_shared/fee-schedules/categories/fee-schedule-template-category";

export const feeScheduleTemplateApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getFeeScheduleTemplateById: build.query<FeeScheduleTemplateJSON, { feeScheduleTemplateId: string }>({
      query: ({ feeScheduleTemplateId }) => `work/fee-schedule-templates/${feeScheduleTemplateId}`,
      rawResponseSchema: FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, error, request) => [{ type: "FeeScheduleTemplate", id: request.feeScheduleTemplateId }],
    }),

    // Every id asked about is answered: a template in `found`, or an `unavailable` entry saying
    // whether it was refused or names nothing (#6122). A refused id used to come back as a row
    // with no name, which failed this schema and with it the whole batch.
    getFeeScheduleTemplatesByIds: build.query<
      ByIdsResultJSON<FeeScheduleTemplateJSON>,
      { feeScheduleTemplateIds: string[] }
    >({
      query: ({ feeScheduleTemplateIds }) => ({
        url: "work/fee-schedule-templates/get-by-ids",
        method: "POST",
        body: feeScheduleTemplateIds,
      }),
      rawResponseSchema: byIdsResultJSONZ(FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
            // Tag each item
            ...(result.found.map((item) => ({
              type: "FeeScheduleTemplate" as const,
              id: item.id ?? undefined,
            })) ?? []),
            // Tag the list itself
            { type: "FeeScheduleTemplate" as const, id: "LIST" },
          ]
          : [{ type: "FeeScheduleTemplate" as const, id: "LIST" }],
    }),

    getFeeScheduleTemplates: build.query<Paginated<FeeScheduleTemplateJSON>, { query: FeeScheduleTemplateQuery, interfaceName: string }>({
      query: ({ query }) => ({
        url: "work/fee-schedule-templates",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        const { data: feeScheduleTemplatesResponse } = await queryFulfilled;
        for (const feeScheduleTemplateJSON of feeScheduleTemplatesResponse.data) {
          if (!feeScheduleTemplateJSON.id) return;
          dispatch(
            feeScheduleTemplateApi.util.upsertQueryData(
              "getFeeScheduleTemplateById",
              { feeScheduleTemplateId: feeScheduleTemplateJSON.id },
              feeScheduleTemplateJSON,
            ),
          );
        }
      },
      transformResponse: (response: FeeScheduleTemplateJSON[], meta): Paginated<FeeScheduleTemplateJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
            // Tag each item
            ...(result?.data?.map((item) => ({
              type: "FeeScheduleTemplate" as const,
              id: item.id ?? undefined,
            })) ?? []),
            // Tag the list itself
            { type: "FeeScheduleTemplate" as const, id: "LIST" },
          ]
          : [{ type: "FeeScheduleTemplate" as const, id: "LIST" }],
    }),

    getFeeScheduleCategories: build.query<Paginated<FeeScheduleCategoryJSON>, { query: string; pageIndex: number; pageSize: number; proposalId?: string }>({
      // proposalId scopes the read to a proposal the caller reviews: the API authorizes it against
      // that proposal's FeeSchedule grant (need-to-know) instead of demanding entity-wide access.
      query: ({ query, pageIndex, pageSize, proposalId }) => ({
        url: "work/fee-schedule-categories",
        params: { query, pageIndex, pageSize, ...(proposalId ? { proposalId } : {}) },
      }),
      rawResponseSchema: z.array(FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: FeeScheduleCategoryJSON[], meta): Paginated<FeeScheduleCategoryJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
            ...result.data.map((category) => ({ type: "FeeScheduleCategory" as const, id: category.id ?? undefined })),
            { type: "FeeScheduleCategory" as const, id: "LIST" },
          ]
          : [{ type: "FeeScheduleCategory" as const, id: "LIST" }],
    }),
    createFeeScheduleCategory: build.mutation<FeeScheduleCategoryJSON, { category: FeeScheduleCategory }>({
      query: ({ category }) => ({
        url: "work/fee-schedule-categories",
        method: "POST",
        // A catalog row is shared by every template in the firm, so it names nobody, and the API
        // refuses a command that does. Who a template's rate is for travels with the template.
        body: new FeeScheduleCategoryCommand(withoutFeeScheduleTeamMember(category)).payload,
      }),
      rawResponseSchema: FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [
        { type: "FeeScheduleCategory" as const, id: "LIST" },
        { type: "FeeScheduleTemplate" as const, id: "LIST" },
      ],
    }),
    updateFeeScheduleCategory: build.mutation<FeeScheduleCategoryJSON, { category: FeeScheduleCategory }>({
      query: ({ category }) => ({
        url: `work/fee-schedule-categories/${category.id.toString()}`,
        method: "PATCH",
        // Without the person, for the reason given on createFeeScheduleCategory.
        body: new FeeScheduleCategoryCommand(withoutFeeScheduleTeamMember(category)).payload,
        // The catalog endpoint binds a command object, not an RFC 6902 document.
        // Override the shared PATCH default so ASP.NET uses its JSON input formatter.
        headers: { "Content-Type": "application/json" },
      }),
      rawResponseSchema: FeeScheduleCategorySchema.FeeScheduleCategoryJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error) => [
        { type: "FeeScheduleCategory" as const, id: "LIST" },
        { type: "FeeScheduleTemplate" as const },
      ],
    }),
    // #endregion

    // #region Create
    // `categorySource` is required, not optional: the caller is the only thing
    // that knows whether these categories are catalog entities or local
    // snapshots, and picking wrong is a 400. See FeeScheduleTemplateCategorySource.
    createFeeScheduleTemplate: build.mutation<
      FeeScheduleTemplateJSON,
      { template: FeeScheduleTemplate; categorySource: FeeScheduleTemplateCategorySource }
    >({
      query: ({ template, categorySource }) => ({
        url: "work/fee-schedule-templates",
        method: "POST",
        body: new FeeScheduleTemplateCommand(template, { categorySource }).payload,
      }),
      rawResponseSchema: FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [
        { type: "FeeScheduleTemplate", id: "LIST" },
      ]
    }),
    // #endregion

    // #region Update
    updateFeeScheduleTemplate: build.mutation<
      FeeScheduleTemplateJSON,
      { templateId: string; originalTemplate: FeeScheduleTemplate; updatedTemplate: FeeScheduleTemplate }
    >({
      query: ({ templateId, originalTemplate, updatedTemplate }) => ({
        url: `work/fee-schedule-templates/${templateId}`,
        method: "PATCH",
        body: new UpdateFeeScheduleTemplateCommand(originalTemplate, updatedTemplate).payload,
        headers: {
          "Content-Type": "application/json-patch+json",
          "If-Match": `"${originalTemplate.version}"`,
        },
      }),
      rawResponseSchema: FeeScheduleTemplateSchema.FeeScheduleTemplateJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async ({ templateId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: feeScheduleTemplateJSON } = await queryFulfilled;
          dispatch(
            feeScheduleTemplateApi.util.upsertQueryData(
              "getFeeScheduleTemplateById",
              { feeScheduleTemplateId: templateId },
              feeScheduleTemplateJSON,
            ),
          );
        } catch {
          // The mutation caller handles and presents request failures.
        }
      },
      invalidatesTags: () => [{ type: "FeeScheduleTemplate" as const, id: "LIST" }],
    }),
    // #endregion

    // #region Delete
    deleteFeeScheduleTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/fee-schedule-templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: "FeeScheduleTemplate" as const, id: templateId },
        { type: "FeeScheduleTemplate" as const, id: "LIST" },
      ],
    }),
    // #endregion

    /**
     * The Templates tab's counts, of the templates and of the archived ones, in one request under the
     * list's own criteria (#6709). `criteria` is the list's filter as search parameters, less the
     * archived state.
     */
    getFeeScheduleTemplateListCounts: build.query<FeeScheduleTemplateListCounts, { criteria: string }>({
      query: ({ criteria }) => `work/fee-schedule-templates/counts${criteria ? `?${criteria}` : ""}`,
      rawResponseSchema: FeeScheduleTemplateListCountsJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: listCountsTags("FeeScheduleTemplate"),
    }),

    // #region Archive
    archiveFeeScheduleTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/fee-schedule-templates/${templateId}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: "FeeScheduleTemplate" as const, id: templateId },
        { type: "FeeScheduleTemplate" as const, id: "LIST" },
      ],
    }),

    unarchiveFeeScheduleTemplate: build.mutation<void, { templateId: string }>({
      query: ({ templateId }) => ({
        url: `work/fee-schedule-templates/${templateId}/unarchive`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: "FeeScheduleTemplate" as const, id: templateId },
        { type: "FeeScheduleTemplate" as const, id: "LIST" },
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetFeeScheduleTemplatesQuery,
  useGetFeeScheduleCategoriesQuery,
  useCreateFeeScheduleCategoryMutation,
  useUpdateFeeScheduleCategoryMutation,
  useGetFeeScheduleTemplateByIdQuery,
  useGetFeeScheduleTemplatesByIdsQuery,
  useCreateFeeScheduleTemplateMutation,
  useUpdateFeeScheduleTemplateMutation,
  useDeleteFeeScheduleTemplateMutation,
  useArchiveFeeScheduleTemplateMutation,
  useUnarchiveFeeScheduleTemplateMutation,
  useGetFeeScheduleTemplateListCountsQuery,
} = feeScheduleTemplateApi;

/** The Templates tab's counts, from its one counts request (#6709). */
export const useFeeScheduleTemplateListCounts = listCountsHook<{ criteria: string }, FeeScheduleTemplateListCounts>(
  useGetFeeScheduleTemplateListCountsQuery,
);
